/**
 * `/cash/bank-accounts/$bankAccountId` — S30 detail view.
 *
 * **Strict-fidelity rebuild** per playbook §6. Page-scoped CSS in
 * `./bank-account-detail-page.css` lifted verbatim from
 * `prototype/cash/bank-account-detail.html ▸ <style>` (lines 11-648).
 *
 * Mirrors the prototype section-for-section:
 *   .acct-head > .acct-head-text > .acct-title (.acct-ava + h1)
 *                                 + .acct-num (mono + .unmask button)
 *                                 + .pill-row (type / sync / cooling)
 *              + .acct-head-actions (Download statement / Edit)
 *   .detail-body (2-col 1.5fr | 1fr):
 *     LEFT  .card.activity-card > .filters + .activity-table
 *           (.activity-day-head + .act-row × N) + .activity-foot
 *     RIGHT .card.balance OR .card.utilisation (type=credit_card)
 *           .card.quick-actions
 *           .card.meta-rows
 *
 * @see prototype/cash/bank-account-detail.html
 */
import { useMemo, useState } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import {
  ArrowDown,
  ArrowRightLeft,
  ArrowUp,
  Calendar,
  Check,
  Cloud,
  CreditCard,
  Download,
  Eye,
  FileText,
  Pencil,
  Search,
  Shield,
} from "lucide-react";
import { Alert } from "@flint/ui";
import { trpc } from "../../../../lib/trpc.ts";
import { useModule } from "../../../../lib/modules.ts";
import { WorkspaceShell } from "../../../../features/workspace/index.ts";
import { formatDate } from "../../../../lib/format.ts";
import "./bank-account-detail-page.css";

export const Route = createFileRoute("/(workspace)/cash/bank-accounts/$bankAccountId/")({
  component: BankAccountDetailPage,
});

function BankAccountDetailPage() {
  const { bankAccountId } = Route.useParams();
  const accountQ = trpc.bankAccounts.get.useQuery({ bankAccountId });
  const ledgerQ = trpc.bankRecon.listAccountLedger.useQuery({ bankAccountId });

  return (
    <WorkspaceShell
      breadcrumbs={[
        { label: "Cash" },
        { label: "Bank accounts", to: "/cash/bank-accounts" },
        { label: accountQ.data?.account.name ?? "…" },
      ]}
    >
      <div className="bank-account-detail-page">
        {accountQ.isLoading ? (
          <p style={{ color: "var(--muted-local)", fontSize: 14 }}>Loading account…</p>
        ) : accountQ.error ? (
          <Alert intent="bad" title="Couldn't load account" description={accountQ.error.message} />
        ) : accountQ.data ? (
          <DetailBody
            account={accountQ.data.account}
            runningBalancePaise={BigInt(accountQ.data.runningBalancePaise)}
            ledger={ledgerQ.data}
            ledgerLoading={ledgerQ.isLoading}
            ledgerError={ledgerQ.error?.message ?? null}
            bankAccountId={bankAccountId}
          />
        ) : null}
      </div>
    </WorkspaceShell>
  );
}

// ── Detail body ──────────────────────────────────────────

interface AccountWire {
  id: string;
  name: string;
  type: "bank" | "cash" | "credit_card";
  accountNumberLast4: string | null;
  accountNumberPlaintext: string | null;
  ifscPlaintext: string | null;
  openingBalancePaise: string;
  openingBalanceAsOf: string;
  imprestAmountPaise: string | null;
  creditLimitPaise: string | null;
  statementCycleDay: number | null;
  /** KYC fields (migration 0023) — mirrors `bankAccountShape` on the wire. */
  branch: string | null;
  micrCode: string | null;
  swiftBic: string | null;
  holderName: string | null;
  holderType: "company" | "founder_personal" | "partner" | "director" | "other" | null;
  bankDetailsChangedAt: string | null;
  archivedAt: string | null;
  updatedAt: string;
}

interface LedgerWire {
  rows: ReadonlyArray<{
    id: string;
    source: "receipt" | "vendor_payment" | "cash_movement";
    valueDate: string;
    signedPaise: string;
    label: string;
    reference: string | null;
    linkedEntityType: string | null;
    linkedEntityId: string | null;
  }>;
  netMovementPaise: string;
  closingBalancePaise: string;
}

function DetailBody({
  account,
  runningBalancePaise,
  ledger,
  ledgerLoading,
  ledgerError,
  bankAccountId,
}: {
  account: AccountWire;
  runningBalancePaise: bigint;
  ledger: LedgerWire | undefined;
  ledgerLoading: boolean;
  ledgerError: string | null;
  bankAccountId: string;
}) {
  const ava = avatarFor(account);
  const parsed = parseAccountName(account.name);

  return (
    <>
      {/* ── Page header ─────────────────────────────────── */}
      <div className="acct-head">
        <div className="acct-head-text">
          <div className="acct-title">
            <div className={`acct-ava ${ava.classMod}`} aria-hidden="true">
              {ava.label}
            </div>
            <h1>{parsed.bank}</h1>
          </div>
          <div className="acct-num">
            <span className="mono">
              {account.accountNumberLast4
                ? `···${account.accountNumberLast4}`
                : (parsed.acc ?? "— · V1.x")}
            </span>
            <button
              type="button"
              className="unmask"
              disabled
              title="Credential reveal lands in V1.x with the step-up middleware"
            >
              <Eye size={12} strokeWidth={2} aria-hidden />
              Show full
            </button>
          </div>
          <div className="pill-row">
            <span className={`pill ${pillForType(account.type)}`}>
              <span className="dot" aria-hidden="true" />
              {labelForType(account.type)}
            </span>
            <span className="pill sync-manual">
              <span className="dot" aria-hidden="true" />
              Manual entry
              <span className="v1x-tag" style={{ marginLeft: 6 }}>
                V1.x sync
              </span>
            </span>
            {isCoolingOff(account.bankDetailsChangedAt) ? (
              <span className="pill cooling">
                <span className="dot" aria-hidden="true" />
                Bank-detail cooling-off
              </span>
            ) : null}
            {account.archivedAt ? (
              <span className="pill archived">
                <span className="dot" aria-hidden="true" />
                Archived
              </span>
            ) : null}
          </div>
        </div>
        <div className="acct-head-actions">
          <button
            type="button"
            className="btn-secondary"
            disabled
            title="Statement download lands in V1.x once Yodlee adapter ships"
          >
            <Download size={13} strokeWidth={2} aria-hidden />
            Download statement
          </button>
          <Link
            to="/cash/bank-accounts/$bankAccountId/edit"
            params={{ bankAccountId }}
            className="btn-secondary"
          >
            <Pencil size={13} strokeWidth={2} aria-hidden />
            Edit account
          </Link>
        </div>
      </div>

      {/* ── 2-col body ──────────────────────────────────── */}
      <div className="detail-body">
        {/* LEFT — Activity stream */}
        <div>
          <ActivityCard ledger={ledger} loading={ledgerLoading} error={ledgerError} />
        </div>

        {/* RIGHT rail */}
        <aside>
          {account.type === "credit_card" ? (
            <UtilisationCard account={account} runningBalancePaise={runningBalancePaise} />
          ) : (
            <BalanceCard
              account={account}
              runningBalancePaise={runningBalancePaise}
              netMovementPaise={ledger ? BigInt(ledger.netMovementPaise) : 0n}
            />
          )}
          <QuickActionsCard account={account} />
          <MetaCard account={account} />
        </aside>
      </div>
    </>
  );
}

// ── Balance card (bank / cash) ───────────────────────────

function BalanceCard({
  account,
  runningBalancePaise,
  netMovementPaise,
}: {
  account: AccountWire;
  runningBalancePaise: bigint;
  netMovementPaise: bigint;
}) {
  const opening = BigInt(account.openingBalancePaise);
  const imprest = account.imprestAmountPaise ? BigInt(account.imprestAmountPaise) : null;
  const overImprest = imprest !== null && runningBalancePaise > imprest;

  return (
    <section className="card balance">
      <div className="card-head">
        <h3>Balance</h3>
      </div>
      <div className="card-body">
        <div className="bal-now">As of today</div>
        {/* Overdrawn bank/cash account → red. (This card only renders for
            bank/cash; cards use UtilisationCard, which stays neutral.) */}
        <div className={`bal-val ${runningBalancePaise < 0n ? "neg" : ""}`}>
          {formatRupees(runningBalancePaise)}
        </div>
        <div className="bal-row">
          <span>Opening balance</span>
          <span>{formatRupees(opening)}</span>
        </div>
        <div className="bal-row">
          <span>Net movement (last 90d)</span>
          <span className={netMovementPaise >= 0n ? "pos" : "neg"}>
            {formatRupeesSigned(netMovementPaise)}
          </span>
        </div>
        {imprest !== null ? (
          <div className="bal-row">
            <span>Imprest ceiling</span>
            <span className={overImprest ? "neg" : ""}>{formatRupees(imprest)}</span>
          </div>
        ) : null}
        {imprest !== null ? (
          <div className={`recon-note ${overImprest ? "warn" : ""}`}>
            <Shield size={14} strokeWidth={2} aria-hidden />
            {overImprest
              ? "Drawer balance exceeds the imprest ceiling — top up or refund the float"
              : "Drawer balance is within the imprest ceiling"}
          </div>
        ) : (
          <div className="recon-note muted">
            <Shield size={14} strokeWidth={2} aria-hidden />
            Reconciliation status lands in V1.x with statement upload
          </div>
        )}
      </div>
    </section>
  );
}

// ── Utilisation card (credit_card) ───────────────────────

function UtilisationCard({
  account,
  runningBalancePaise,
}: {
  account: AccountWire;
  runningBalancePaise: bigint;
}) {
  const limit = account.creditLimitPaise ? BigInt(account.creditLimitPaise) : 0n;
  const utilisationPct =
    limit === 0n ? 0 : Math.min(100, Number((runningBalancePaise * 10000n) / limit) / 100);
  const headroom = limit - runningBalancePaise;

  return (
    <section className="card utilisation">
      <div className="card-head">
        <h3>Card utilisation</h3>
      </div>
      <div className="card-body">
        <div className="util-now">Outstanding</div>
        <div className="util-val">{formatRupees(runningBalancePaise)}</div>
        <div className="util-of">
          of <strong>{formatRupees(limit)}</strong> sanctioned · {utilisationPct.toFixed(1)}% used
        </div>
        <div className="util-progress">
          <div className="fill" style={{ width: `${utilisationPct}%` }} />
        </div>
        <div className="util-meta">
          <div className="row">
            <span>Headroom</span>
            <span>{formatRupees(headroom)}</span>
          </div>
          <div className="row">
            <span>Credit limit</span>
            <span>{formatRupees(limit)}</span>
          </div>
          {account.statementCycleDay !== null ? (
            <div className="row">
              <span>Statement cycle</span>
              <span>Day {account.statementCycleDay}</span>
            </div>
          ) : null}
        </div>
        {account.statementCycleDay !== null ? (
          <div className="cycle-pill">
            <Calendar size={12} strokeWidth={2} aria-hidden />
            Statement cuts day {account.statementCycleDay} each month
          </div>
        ) : null}
      </div>
    </section>
  );
}

// ── Quick actions ────────────────────────────────────────

function QuickActionsCard({ account }: { account: AccountWire }) {
  const isCard = account.type === "credit_card";
  // Cash movements, transfers, and reconcile are the 1.0-withheld cash module
  // (ADR-0046). Bank accounts stay in, but these actions dead-end at the route
  // guard's redirect when cash is off — so hide them.
  const hasCash = useModule("cash");
  return (
    <section className="card quick-actions">
      <div className="card-head">
        <h3>Quick actions</h3>
      </div>
      <div className="card-body">
        <div className="qa-list">
          {/* Pay bill (cards only) — deep-links to a transfer pre-filled with
              this card as the destination; `from` defaults to the primary
              bank. The transfer flow shows outstanding + min-due and pays the
              liability down. */}
          {isCard && hasCash ? (
            <Link
              to="/cash/transfers/new"
              search={{ to: account.id }}
              className="qa-btn is-primary"
            >
              <div className="qa-ic">
                <CreditCard size={14} strokeWidth={2} aria-hidden />
              </div>
              <div className="qa-text">
                <strong>Pay bill</strong>
                <span>Settle this card from a bank account</span>
              </div>
            </Link>
          ) : null}
          {hasCash ? (
            <Link
              to="/cash/movements/new"
              className={`qa-btn ${isCard ? "" : "is-primary"}`}
            >
              <div className="qa-ic">
                <ArrowRightLeft size={14} strokeWidth={2} aria-hidden />
              </div>
              <div className="qa-text">
                <strong>Record cash movement</strong>
                <span>Transfer / statutory / petty cash / bank charge / etc.</span>
              </div>
            </Link>
          ) : null}
          <button type="button" className="qa-btn" disabled title="Yodlee bank sync lands in V1.x">
            <div className="qa-ic">
              <Cloud size={14} strokeWidth={2} aria-hidden />
            </div>
            <div className="qa-text">
              <strong>Sync from bank</strong>
              <span>Pull statement rows automatically</span>
            </div>
            <span className="v1x-tag">V1.x</span>
          </button>
          <button
            type="button"
            className="qa-btn"
            disabled
            title="Statement generation lands in V1.x"
          >
            <div className="qa-ic">
              <FileText size={14} strokeWidth={2} aria-hidden />
            </div>
            <div className="qa-text">
              <strong>Generate statement</strong>
              <span>PDF + CSV for the period</span>
            </div>
            <span className="v1x-tag">V1.x</span>
          </button>
          {hasCash ? (
            <Link to="/cash/reconcile" className="qa-btn">
              <div className="qa-ic">
                <Shield size={14} strokeWidth={2} aria-hidden />
              </div>
              <div className="qa-text">
                <strong>Reconcile this account</strong>
                <span>Match statement rows against the ledger</span>
              </div>
            </Link>
          ) : null}
        </div>
      </div>
    </section>
  );
}

// ── Meta card ────────────────────────────────────────────

function MetaCard({ account }: { account: AccountWire }) {
  return (
    <section className="card">
      <div className="card-head">
        <h3>Details</h3>
      </div>
      <div className="card-body">
        <div className="meta-rows">
          <div className="meta-row">
            <span>Type</span>
            <span>{labelForType(account.type)}</span>
          </div>
          <div className="meta-row">
            <span>Opened</span>
            <span>{formatDate(account.openingBalanceAsOf)}</span>
          </div>
          {account.accountNumberLast4 ? (
            <div className="meta-row">
              <span>Account number</span>
              <span className="mono">···{account.accountNumberLast4}</span>
            </div>
          ) : null}
          {account.statementCycleDay !== null ? (
            <div className="meta-row">
              <span>Statement cycle</span>
              <span>Day {account.statementCycleDay}</span>
            </div>
          ) : null}
          {account.imprestAmountPaise ? (
            <div className="meta-row">
              <span>Imprest ceiling</span>
              <span>{formatRupees(BigInt(account.imprestAmountPaise))}</span>
            </div>
          ) : null}
          {account.creditLimitPaise ? (
            <div className="meta-row">
              <span>Credit limit</span>
              <span>{formatRupees(BigInt(account.creditLimitPaise))}</span>
            </div>
          ) : null}
          {account.holderName ? (
            <div className="meta-row">
              <span>Account holder</span>
              <span>{account.holderName}</span>
            </div>
          ) : null}
          {account.holderType ? (
            <div className="meta-row">
              <span>Holder type</span>
              <span>{labelForHolderType(account.holderType)}</span>
            </div>
          ) : null}
          {account.branch ? (
            <div className="meta-row">
              <span>Branch</span>
              <span>{account.branch}</span>
            </div>
          ) : null}
          {account.micrCode ? (
            <div className="meta-row">
              <span>MICR</span>
              <span className="mono">{account.micrCode}</span>
            </div>
          ) : null}
          {account.swiftBic ? (
            <div className="meta-row">
              <span>SWIFT / BIC</span>
              <span className="mono">{account.swiftBic}</span>
            </div>
          ) : null}
          <div className="meta-row">
            <span>Last credentials change</span>
            <span>
              {account.bankDetailsChangedAt
                ? formatDate(account.bankDetailsChangedAt.slice(0, 10))
                : "—"}
            </span>
          </div>
          <div className="meta-row">
            <span>Currency</span>
            <span className="mono">INR</span>
          </div>
        </div>
      </div>
    </section>
  );
}

// ── Activity card (left column) ──────────────────────────

function ActivityCard({
  ledger,
  loading,
  error,
}: {
  ledger: LedgerWire | undefined;
  loading: boolean;
  error: string | null;
}) {
  const [filter, setFilter] = useState<"all" | "in" | "out" | "xfer">("all");
  const [query, setQuery] = useState("");

  const filtered = useMemo(
    () => filterRows(ledger?.rows ?? [], filter, query),
    [ledger, filter, query],
  );
  const grouped = useMemo(() => groupByDay(filtered), [filtered]);

  return (
    <>
      <div className="filters">
        <button
          type="button"
          className={`filter-pill ${filter === "all" ? "is-active" : ""}`}
          onClick={() => setFilter("all")}
        >
          All
        </button>
        <button
          type="button"
          className={`filter-pill ${filter === "in" ? "is-active" : ""}`}
          onClick={() => setFilter("in")}
        >
          <ArrowDown size={12} strokeWidth={2} aria-hidden />
          Money in
        </button>
        <button
          type="button"
          className={`filter-pill ${filter === "out" ? "is-active" : ""}`}
          onClick={() => setFilter("out")}
        >
          <ArrowUp size={12} strokeWidth={2} aria-hidden />
          Money out
        </button>
        <button
          type="button"
          className={`filter-pill ${filter === "xfer" ? "is-active" : ""}`}
          onClick={() => setFilter("xfer")}
        >
          <ArrowRightLeft size={14} strokeWidth={2} aria-hidden />
          Transfers
        </button>
        <label className="filter-search">
          <Search size={12} strokeWidth={2} aria-hidden />
          <input
            type="search"
            placeholder="Search activity…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
        </label>
        <span className="filter-count">
          {filtered.length} {filtered.length === 1 ? "event" : "events"}
        </span>
      </div>

      <section className="card">
        <div className="card-body activity-table" style={{ padding: 0 }}>
          {loading ? (
            <div className="activity-empty">
              <span>Loading activity…</span>
            </div>
          ) : error ? (
            <div className="activity-empty">
              <strong>Couldn't load activity</strong>
              <span>{error}</span>
            </div>
          ) : grouped.length === 0 ? (
            <div className="activity-empty">
              <strong>No activity yet</strong>
              <span>
                Receipts, vendor payments, and cash movements referencing this account will appear
                here.
              </span>
            </div>
          ) : (
            grouped.map((day) => (
              <div key={day.date}>
                <div className="activity-day-head">
                  <span>{formatLongDate(day.date)}</span>
                  <span className="day-total">
                    Day total{" "}
                    <span className={day.totalPaise >= 0n ? "pos" : "neg"}>
                      {formatRupeesSigned(day.totalPaise)}
                    </span>
                  </span>
                </div>
                {day.rows.map((r) => (
                  <ActivityRow key={`${r.source}:${r.id}`} row={r} />
                ))}
              </div>
            ))
          )}
        </div>
        {filtered.length > 0 ? (
          <div className="activity-foot">
            <span>
              {filtered.length} {filtered.length === 1 ? "event" : "events"} in the last 90 days
            </span>
            <button type="button" className="btn-ghost" disabled title="Pagination lands in V1.0.x">
              Load more
            </button>
          </div>
        ) : null}
      </section>
    </>
  );
}

function ActivityRow({ row }: { row: LedgerWire["rows"][number] }) {
  const signedPaise = BigInt(row.signedPaise);
  const isOut = signedPaise < 0n;
  const isXfer = row.source === "cash_movement" && row.label.toLowerCase().includes("transfer");
  const sourceCls = isXfer ? "s-info" : isOut ? "s-rose" : "s-emerald";
  const icCls = isXfer ? "xfer" : isOut ? "out" : "in";

  return (
    <div className={`act-row ${sourceCls}`}>
      <div className={`act-ic ${icCls}`} aria-hidden="true">
        {isXfer ? (
          <ArrowRightLeft size={14} strokeWidth={2} aria-hidden />
        ) : isOut ? (
          <ArrowUp size={12} strokeWidth={2} aria-hidden />
        ) : (
          <ArrowDown size={12} strokeWidth={2} aria-hidden />
        )}
      </div>
      <div className="act-text">
        <div className="act-title">{row.label}</div>
        <div className="act-sub">
          <span
            style={{
              textTransform: "uppercase",
              letterSpacing: ".04em",
              fontSize: 12,
              fontWeight: 600,
            }}
          >
            {labelForSource(row.source)}
          </span>
          {row.reference ? (
            <>
              <span className="dot" />
              <span className="mono">{row.reference}</span>
            </>
          ) : null}
        </div>
      </div>
      <div className={`act-amount ${signedPaise >= 0n ? "pos" : "neg"}`}>
        {formatRupees(absBigInt(signedPaise))}
        <span className="ts">{formatDate(row.valueDate)}</span>
      </div>
      <div className="act-tail">
        <span className="tail-pill ok">
          <Check size={10} strokeWidth={3} aria-hidden />
          Booked
        </span>
      </div>
    </div>
  );
}

// ── Helpers ──────────────────────────────────────────────

function pillForType(t: AccountWire["type"]): string {
  switch (t) {
    case "cash":
      return "type-cash";
    case "credit_card":
      return "type-cc";
    default:
      return "type-bank";
  }
}
function labelForType(t: AccountWire["type"]): string {
  switch (t) {
    case "cash":
      return "Petty cash";
    case "credit_card":
      return "Credit card";
    default:
      return "Operating bank";
  }
}
function labelForHolderType(t: NonNullable<AccountWire["holderType"]>): string {
  switch (t) {
    case "founder_personal":
      return "Founder · personal";
    // TODO(V1.0.x): `partner`, `director` and `other` all fall through to
    // "Company", which mislabels them on a KYC surface. Needs a copy call.
    default:
      return "Company";
  }
}
function labelForSource(s: "receipt" | "vendor_payment" | "cash_movement"): string {
  switch (s) {
    case "receipt":
      return "Receipt";
    case "vendor_payment":
      return "Vendor payment";
    case "cash_movement":
      return "Cash movement";
  }
}

function avatarFor(account: AccountWire): { classMod: string; label: string } {
  if (account.type === "cash") return { classMod: "b-cash", label: "₹" };
  if (account.type === "credit_card") return { classMod: "b-cc", label: "VISA" };
  const n = account.name.toLowerCase();
  if (n.includes("hdfc")) return { classMod: "b-hdfc", label: "H" };
  if (n.includes("icici")) return { classMod: "b-icici", label: "I" };
  if (n.includes("kotak")) return { classMod: "b-kotak", label: "K" };
  if (n.includes("axis")) return { classMod: "b-axis", label: "A" };
  if (n.includes("rbl")) return { classMod: "b-rbl", label: "R" };
  return { classMod: "b-hdfc", label: account.name.charAt(0).toUpperCase() };
}

function parseAccountName(name: string): { bank: string; acc: string | null } {
  const sep = " · ";
  const idx = name.indexOf(sep);
  if (idx === -1) return { bank: name, acc: null };
  return { bank: name.slice(0, idx), acc: name.slice(idx + sep.length) };
}

function isCoolingOff(bankDetailsChangedAt: string | null): boolean {
  if (!bankDetailsChangedAt) return false;
  const COOLING_OFF_MS = 24 * 60 * 60 * 1000;
  const changedAt = new Date(bankDetailsChangedAt).getTime();
  return Date.now() - changedAt < COOLING_OFF_MS;
}

function filterRows(
  rows: LedgerWire["rows"],
  filter: "all" | "in" | "out" | "xfer",
  query: string,
): LedgerWire["rows"] {
  const q = query.trim().toLowerCase();
  return rows.filter((r) => {
    const isOut = BigInt(r.signedPaise) < 0n;
    const isXfer = r.source === "cash_movement" && r.label.toLowerCase().includes("transfer");
    switch (filter) {
      case "in":
        if (isOut || isXfer) return false;
        break;
      case "out":
        if (!isOut || isXfer) return false;
        break;
      case "xfer":
        if (!isXfer) return false;
        break;
    }
    if (!q) return true;
    return r.label.toLowerCase().includes(q) || (r.reference ?? "").toLowerCase().includes(q);
  });
}

function groupByDay(
  rows: LedgerWire["rows"],
): Array<{ date: string; rows: LedgerWire["rows"]; totalPaise: bigint }> {
  const byDay = new Map<string, { rows: LedgerWire["rows"][number][]; totalPaise: bigint }>();
  for (const r of rows) {
    const bucket = byDay.get(r.valueDate);
    const paise = BigInt(r.signedPaise);
    if (bucket) {
      bucket.rows.push(r);
      bucket.totalPaise += paise;
    } else {
      byDay.set(r.valueDate, { rows: [r], totalPaise: paise });
    }
  }
  // Already sorted by valueDate desc from the server.
  return Array.from(byDay.entries()).map(([date, v]) => ({
    date,
    rows: v.rows,
    totalPaise: v.totalPaise,
  }));
}

function formatLongDate(iso: string): string {
  const d = new Date(`${iso}T00:00:00Z`);
  const today = new Date();
  const todayIso = today.toISOString().slice(0, 10);
  const yIso = new Date(today.getTime() - 86400000).toISOString().slice(0, 10);
  if (iso === todayIso) return "Today";
  if (iso === yIso) return "Yesterday";
  return d.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
}

function absBigInt(n: bigint): bigint {
  return n < 0n ? -n : n;
}

function formatRupees(paise: bigint): string {
  const sign = paise < 0n ? "−" : "";
  const abs = absBigInt(paise);
  const rupees = abs / 100n;
  const paiseRemainder = abs % 100n;
  // Show paise only when non-zero, so round amounts stay clean (₹18,000) but
  // sub-rupee / tail amounts read correctly (₹0.99) instead of flooring to ₹0.
  const fraction = paiseRemainder === 0n ? "" : `.${paiseRemainder.toString().padStart(2, "0")}`;
  return `${sign}₹${rupees.toLocaleString("en-IN")}${fraction}`;
}

function formatRupeesSigned(paise: bigint): string {
  if (paise === 0n) return "₹0";
  return formatRupees(paise);
}

/* ── Fidelity report (playbook §6.3) ─────────────────────────────

   Prototype: prototype/cash/bank-account-detail.html (1,290 lines)

   ## .acct-head (lines 759-798)
   - Verbatim: .acct-head > .acct-head-text > .acct-title (.acct-ava + h1)
     + .acct-num (.mono + .unmask) + .pill-row (.pill.type-{bank|cash|cc}
     + .pill.sync-{auto|csv|manual} + .pill.cooling + .pill.archived)
     + .acct-head-actions (Download statement disabled + Edit Link)
   - Diffs:
     · `.acct-ava` carries a palette modifier matching the row palette
       on the list view (b-hdfc/b-icici/b-kotak/b-axis/b-rbl/b-cash/b-cc).
     · "Show full" credential unmask is disabled (step-up middleware V1.x).
     · The sync pill says "Manual entry" with a V1.x tag — the prototype's
       "Auto-sync" is not yet earned; we mark it honestly.

   ## .detail-body grid (line 802-end)
   - Verbatim: 1.5fr | 1fr grid, collapses to 1fr at ≤1080px.
   - LEFT: .filters + .card .activity-table > (.activity-day-head + .act-row × N) + .activity-foot
   - RIGHT: .card.balance OR .card.utilisation + .card.quick-actions + .card with .meta-rows

   ## .balance card (lines 337-364)
   - Real running balance + opening balance + 90d net movement.
   - Imprest ceiling row + recon-note for type=cash.
   - The recon-note for type=bank is muted with V1.x copy (statement match deferred).

   ## .utilisation card (lines 619-630)
   - Real outstanding + credit-limit + utilisation% + headroom + statement cycle.
   - The full coloured gradient progress bar mirrors the prototype.

   ## .quick-actions (lines 367-396)
   - .qa-btn.is-primary: Record cash movement → real Link.
   - .qa-btn (disabled, V1.x tag): Sync from bank, Generate statement.
   - .qa-btn: Reconcile this account → real Link to /reconcile preview.

   ## .meta-rows (lines 397-407)
   - Type / Opened / Account number / Statement cycle / Imprest / Credit limit /
     Last credentials change / Currency — only rows we have data for render.

   ## Activity stream (lines 410-525)
   - .filters: All / Money in / Money out / Transfers + .filter-search + .filter-count.
   - .activity-table: grouped by day (Today / Yesterday / DD MMM YYYY) with day-total.
   - .act-row: source-coloured stripe + .act-ic (in/out/xfer) + .act-text
     (.act-title + .act-sub) + .act-amount (.pos with +sign / .neg) +
     .act-tail (.tail-pill.ok "Booked").
   - .activity-foot with disabled "Load more" (pagination V1.0.x).
   - Empty + loading + error states.

──────────────────────────────────────────────────────────────── */
